import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap";
import moment from "moment";
import AccountStatus from "./AccountStatus";
import { useDispatch } from "react-redux";
import * as action from '../../store/actions'
const ProviderModal = (props) => {
  const { isOpen, toggle, providerData } = props; // Destructure providerData from props
  if (!providerData) {
    return null; // Return null if no order data is available
  }

  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const dispatch = useDispatch()
  const toggleViewStatusModal = (accountId, status) => {
    setSelectedAccountId(accountId);
    setSelectedStatus(status);
    setStatusModalOpen(!statusModalOpen);
  };

  const toggleStatusModal = () => {
    setStatusModalOpen(!statusModalOpen);
  };

  const updateAccountStatus = (accountId, status) => {
    dispatch(action.updateAccountStatusRequest(accountId, status));
    toggleStatusModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      role="dialog"
      autoFocus={true}
      centered={true}
      className="exampleModal"
      tabIndex="-1"
      toggle={toggle}
    >
      <div className="modal-content">
        <ModalHeader toggle={toggle}>تفاصيل الحساب</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <small className="text-muted">تاريخ التسجيل</small>
                <p className="mb-0 fw-bold">
                  {moment(providerData.createdAt || providerData.sub_date).format("DD MMMM, YYYY")}
                </p>
              </div>
              <div>
                <small className="text-muted">حالة الحساب</small>
                <div className="mt-1">
                  <Button size="sm" className={getStatusButtonClass(providerData.account_status)}>
                    {translateStatus(providerData.account_status)}
                  </Button>
                </div>
              </div>
            </div>

            {/* Order Cancellation Alert */}
            <div className={`alert ${getCancellationAlertClass(providerData.order_cancellation_count)} mb-0 d-flex align-items-center justify-content-between`} role="alert">
              <div className="d-flex align-items-center">
                <i className={`${getCancellationIcon(providerData.order_cancellation_count)} me-2 fs-5`}></i>
                <div>
                  <strong>عدد الطلبات الملغاة</strong>
                  <p className="mb-0 small">{getCancellationMessage(providerData.order_cancellation_count)}</p>
                </div>
              </div>
              <div>
                <span className="badge bg-dark fs-5 px-3 py-2">
                  {providerData.order_cancellation_count || 0}
                </span>
              </div>
            </div>
          </div>

          <hr />

          <div className="table-responsive">
            <Table className="table table-borderless align-middle mb-0">
              <tbody>
                {/* Personal Information Section */}
                <tr className="bg-light">
                  <td colSpan="2" className="py-2">
                    <strong className="text-primary">المعلومات الشخصية</strong>
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "40%" }} className="text-muted">الاسم الثلاثي</td>
                  <td className="fw-medium">{providerData.full_name || "غير متوفر"}</td>
                </tr>
                <tr>
                  <td className="text-muted">البريد الإلكتروني</td>
                  <td className="fw-medium">{providerData.email || "غير متوفر"}</td>
                </tr>
                <tr>
                  <td className="text-muted">رقم الهاتف</td>
                  <td className="fw-medium" style={{ direction: "ltr", textAlign: "right" }}>
                    {providerData.mobile || "غير متوفر"}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">معرف المزود</td>
                  <td className="fw-medium">
                    <code className="text-dark">{providerData.id}</code>
                  </td>
                </tr>

                {/* Business Information Section */}
                <tr className="bg-light">
                  <td colSpan="2" className="py-2 pt-3">
                    <strong className="text-primary">معلومات الصالون</strong>
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">اسم الصالون</td>
                  <td className="fw-medium">{providerData.salon_name || "غير متوفر"}</td>
                </tr>
                <tr>
                  <td className="text-muted">عنوان الفرع</td>
                  <td className="fw-medium">{providerData.address || "غير متوفر"}</td>
                </tr>
                {providerData.description && (
                  <tr>
                    <td className="text-muted">الوصف</td>
                    <td className="fw-medium">{providerData.description}</td>
                  </tr>
                )}

                {/* Tax & Registration Information Section */}
                <tr className="bg-light">
                  <td colSpan="2" className="py-2 pt-3">
                    <strong className="text-primary">معلومات التسجيل التجاري</strong>
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">رقم السجل التجاري</td>
                  <td className="fw-medium" style={{ direction: "ltr", textAlign: "right" }}>
                    {providerData.commercial_register || "غير متوفر"}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">الرقم الضريبي</td>
                  <td className="fw-medium" style={{ direction: "ltr", textAlign: "right" }}>
                    {providerData.tax_number || "غير متوفر"}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">الرقم الموحد للمنشأة</td>
                  <td className="fw-medium" style={{ direction: "ltr", textAlign: "right" }}>
                    {providerData.entity_unified_no || "غير متوفر"}
                  </td>
                </tr>

                {/* Banking Information Section */}
                <tr className="bg-light">
                  <td colSpan="2" className="py-2 pt-3">
                    <strong className="text-primary">المعلومات البنكية</strong>
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">اسم الحساب</td>
                  <td className="fw-medium">{providerData.account_name || "غير متوفر"}</td>
                </tr>
                <tr>
                  <td className="text-muted">رقم الحساب البنكي</td>
                  <td className="fw-medium" style={{ direction: "ltr", textAlign: "right" }}>
                    {providerData.bank_account || "غير متوفر"}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
          
          <AccountStatus
            isOpen={statusModalOpen}
            toggle={toggleViewStatusModal}
            accountId={selectedAccountId}
            status={selectedStatus}
            updateAccountStatus={updateAccountStatus}
          />
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="secondary" onClick={toggle}>
            إغلاق
          </Button>
          <Button
              className="btn btn-primary  "
              onClick={() =>
                toggleViewStatusModal(providerData.id, providerData.account_status)
              }
            >
              تغير حالة الحساب
            </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

ProviderModal.propTypes = {
  toggle: PropTypes.func,
  isOpen: PropTypes.bool,
  providerData: PropTypes.object,
};

const translateStatus = status => {
  const translations = {
    active: "نشط",
    paused_for_subscription_expiry: "انتهاء صلاحية الاشتراك",
    action_required: "غير نشط",
    // Add more translations as needed
  }
  return translations[status] || status
}

const getStatusButtonClass = (accountStatus) => {
  switch (accountStatus) {
    case "active":
      return "btn-success";
    case "paused_for_subscription_expiry":
      return "btn-danger";
    case "action_required":
      return "btn-warning";
    default:
      return "btn-secondary";
  }
};

const getCancellationAlertClass = (count) => {
  if (count === 0 || !count) return "alert-success";
  if (count <= 3) return "alert-info";
  if (count <= 7) return "alert-warning";
  return "alert-danger";
};

const getCancellationIcon = (count) => {
  if (count === 0 || !count) return "mdi mdi-check-circle";
  if (count <= 3) return "mdi mdi-information";
  if (count <= 7) return "mdi mdi-alert";
  return "mdi mdi-alert-octagon";
};

const getCancellationMessage = (count) => {
  if (count === 0 || !count) return "لا توجد طلبات ملغاة - أداء ممتاز";
  if (count <= 3) return "عدد قليل من الإلغاءات - أداء جيد";
  if (count <= 7) return "يحتاج إلى متابعة - عدد متوسط من الإلغاءات";
  return "يتطلب إجراء عاجل - عدد مرتفع من الإلغاءات";
};

export default ProviderModal;
